import { useEffect, useState } from 'react';
import { Wallet, ArrowDownCircle, ChevronRight, Loader2, ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';

export const OutstandingPayablesWidget = ({ currency = 'SAR' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);        // selected payable row
  const [entries, setEntries] = useState(null);
  const [entriesLoading, setEntriesLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get('/accounting/outstanding-payables');
        if (alive) setData(data);
      } catch { /* non-critical widget */ }
      finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []);

  const fmt = (n) => `${currency} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const payables = data?.payables || [];

  const openDrilldown = async (p) => {
    setActive(p);
    setEntries(null);
    setEntriesLoading(true);
    try {
      const { data } = await api.get('/accounting/payable-entries', { params: { account: p.account } });
      setEntries(data);
    } catch {
      setEntries({ entries: [] });
    } finally {
      setEntriesLoading(false);
    }
  };

  const payNow = (p) => {
    // Deep-link to the Expenses page and auto-open the Add Expense dialog so
    // the user can record a settlement against this liability account. The
    // category resolver posts a debit to the payable, clearing the balance.
    navigate(`/expenses?action=add&settle=${encodeURIComponent(p.account)}`);
  };

  if (loading) return null;
  if (payables.length === 0) return null;

  return (
    <>
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="outstanding-payables-widget">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-rose-50 text-rose-600 flex items-center justify-center">
              <Wallet className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-semibold text-foreground text-sm">Outstanding Payables</h3>
              <p className="text-[11px] text-muted-foreground">What you still owe · settled in {data?.month}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[11px] text-muted-foreground">Total outstanding</p>
            <p className="text-lg font-bold text-rose-600 tabular-nums" data-testid="payables-total-outstanding">{fmt(data?.total_outstanding)}</p>
          </div>
        </div>

        <div className="space-y-2">
          {payables.map((p) => (
            <button
              key={p.account}
              type="button"
              onClick={() => openDrilldown(p)}
              className="w-full flex items-center justify-between py-2 px-3 rounded-lg bg-muted/40 hover:bg-muted transition-colors text-left group"
              data-testid={`payable-row-${p.account_code || p.account}`}
            >
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{p.account}</p>
                {p.account_code && <p className="text-[10px] font-mono text-muted-foreground">{p.account_code}</p>}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <div className="text-right">
                  <p className="text-sm font-semibold tabular-nums text-foreground">{fmt(p.outstanding)}</p>
                  {p.settled_this_month > 0 && (
                    <p className="text-[11px] text-emerald-600 flex items-center gap-1 justify-end">
                      <ArrowDownCircle className="w-3 h-3" /> {fmt(p.settled_this_month)} paid
                    </p>
                  )}
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground transition-colors" />
              </div>
            </button>
          ))}
        </div>

        <div className="mt-3 pt-3 border-t border-border flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Settled this month</span>
          <span className="font-semibold text-emerald-600 tabular-nums" data-testid="payables-total-settled">{fmt(data?.total_settled_month)}</span>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setEntries(null); } }}>
        <DialogContent className="max-w-2xl" data-testid="payable-drilldown-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wallet className="w-5 h-5 text-rose-600" />
              {active?.account}
            </DialogTitle>
            <DialogDescription>
              Outstanding {fmt(active?.outstanding)} · {active?.account_code || ''} — the entries that make up this balance.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end -mt-2 mb-1">
            <button
              type="button"
              onClick={() => active && payNow(active)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium px-3 py-1.5 transition-colors"
              data-testid="payable-pay-now-button"
            >
              <ArrowUpRight className="w-4 h-4" /> Pay now
            </button>
          </div>

          <div className="max-h-[55dvh] overflow-auto rounded-lg border border-border">
            {entriesLoading ? (
              <div className="flex items-center justify-center py-12 text-muted-foreground">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading entries…
              </div>
            ) : (entries?.entries || []).length === 0 ? (
              <div className="py-12 text-center text-sm text-muted-foreground" data-testid="payable-entries-empty">
                No entries found for this account.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="payable-entries-table">
                  <thead className="bg-muted/60 text-[11px] uppercase tracking-wide text-muted-foreground sticky top-0">
                    <tr>
                      <th className="text-left font-medium px-3 py-2">Date</th>
                      <th className="text-left font-medium px-3 py-2">Description</th>
                      <th className="text-left font-medium px-3 py-2">Branch</th>
                      <th className="text-right font-medium px-3 py-2">Credit (owe)</th>
                      <th className="text-right font-medium px-3 py-2">Debit (paid)</th>
                      <th className="text-right font-medium px-3 py-2">Balance</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(entries?.entries || []).map((e, i) => (
                      <tr key={i} className="border-t border-border hover:bg-muted/30" data-testid={`payable-entry-row-${i}`}>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-muted-foreground">{(e.date || '').slice(0, 10)}</td>
                        <td className="px-3 py-2">{e.description}</td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">{e.branch}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-rose-600">{e.credit ? fmt(e.credit) : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums text-emerald-600">{e.debit ? fmt(e.debit) : '—'}</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{fmt(e.running_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
